import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { socket } from '../socket';
import DashboardLayout from '../components/DashboardLayout';
import PatternPreview from '../components/PatternPreview';
import { setActiveRoomCode } from '../utils/activeRoomCode';
import { styles } from './GameSetupPage.styles';

const PATTERNS = [
  { id: 'line',         label: 'Line',         description: 'Any row, column, or diagonal', sizes: [4,5] },
  { id: 'blackout',     label: 'Blackout',      description: 'Every square',                sizes: [4,5] },
  { id: 'four_corners', label: 'Four Corners',  description: 'All four corners',            sizes: [4,5] },
  { id: 'x',            label: 'X',             description: 'Both diagonals',              sizes: [4,5] },
  { id: 'plus',         label: 'Plus',          description: 'Center row and column',       sizes: [5]   },
  { id: 't',            label: 'T',             description: 'Top row and center column',   sizes: [5]   },
  { id: 'l',            label: 'L',             description: 'Left column and bottom row',  sizes: [4,5] },
  { id: 'frame',        label: 'Frame',         description: 'All border squares',          sizes: [4,5] },
];

function VocabCard({ title, meta, wordCount, selected, onSelect }) {
  const tooFew = wordCount < 25;
  return (
    <button
      className="setup-novel"
      onClick={() => { if (!tooFew) onSelect(); }}
      disabled={tooFew}
      style={{
        ...styles.novelCard,
        ...(selected ? styles.novelCardSelected : {}),
        ...(tooFew ? styles.novelCardDisabled : {}),
      }}
    >
      <div style={styles.novelTitle}>{title}</div>
      {meta != null && <div style={styles.novelMeta}>{meta}</div>}
      <div style={tooFew ? styles.wordCountLow : styles.wordCountNeutral}>
        {wordCount} words{tooFew ? ' (need 25+)' : ''}
      </div>
    </button>
  );
}

export default function GameSetupPage() {
  const { teacher, isSubscriber } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [novels, setNovels] = useState([]);
  const [customLists, setCustomLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const incomingCustomListId = location.state?.customListId || '';
  const incomingCustomListName = location.state?.customListName || '';
  const [listSource, setListSource] = useState(incomingCustomListId ? 'custom' : 'novel');
  const [selectedNovelId, setSelectedNovelId] = useState('');
  const [selectedCustomListId, setSelectedCustomListId] = useState(incomingCustomListId);
  const [boardSize, setBoardSize] = useState(null);
  const [freeSpace, setFreeSpace] = useState(null);
  const [displayMode, setDisplayMode] = useState(null);
  const [pattern, setPattern] = useState(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [existingGameCode, setExistingGameCode] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [novelError, setNovelError] = useState(false);
  const [hoveredPattern, setHoveredPattern] = useState('');
  const [gridAtBottom, setGridAtBottom] = useState(false);
  const novelSectionRef = useRef(null);
  const boardSizeSectionRef = useRef(null);
  const displayModeSectionRef = useRef(null);
  const winPatternSectionRef = useRef(null);
  const gridScrollRef = useRef(null);
  const scrolledRef = useRef({ board: false, display: false, pattern: false });
  const pendingConnectRef = useRef(null);
  const pendingConnectErrorRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pendingConnectRef.current) socket.off('connect', pendingConnectRef.current);
      if (pendingConnectErrorRef.current) socket.off('connect_error', pendingConnectErrorRef.current);
    };
  }, []);

  useEffect(() => {
    const fetches = [
      fetch('/api/teacher/novels', { credentials: 'include' }).then(r => r.json()),
    ];
    if (isSubscriber) {
      fetches.push(fetch('/api/teacher/lists', { credentials: 'include' }).then(r => r.json()));
    }
    Promise.all(fetches)
      .then(([novelData, listData]) => {
        setNovels(novelData.novels || []);
        if (listData) setCustomLists(listData.lists || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load data. Please refresh.');
        setLoading(false);
      });
  }, [isSubscriber]);

  useLayoutEffect(() => {
    const el = gridScrollRef.current;
    if (!el) return;
    setGridAtBottom(el.scrollHeight <= el.clientHeight + 2);
  }, [listSource, search, novels, customLists]);

  function handleGridScroll(e) {
    const el = e.currentTarget;
    setGridAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  }

  async function handleCustomize(novel) {
    setCloning(true);
    setError('');
    try {
      const res = await fetch('/api/teacher/lists/clone-novel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ novelId: novel.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not clone novel. Please try again.');
        setCloning(false);
        return;
      }
      navigate(`/lists/${data.id}/edit`, { state: { returnToSetup: true } });
    } catch {
      setError('Could not connect. Please try again.');
      setCloning(false);
    }
  }

  const usingCustom = listSource === 'custom';
  const hasSelection = usingCustom ? !!selectedCustomListId : !!selectedNovelId;
  const selectedVocabItem = usingCustom
    ? customLists.find((l) => l.id === selectedCustomListId)
    : novels.find((n) => n.id === selectedNovelId);
  const selectedSourceLabel = usingCustom
    ? (selectedVocabItem?.name ?? incomingCustomListName ?? null)
    : (selectedVocabItem?.title ?? null);
  const displayModeLabel = displayMode === 'word' ? 'Words' : displayMode === 'definition' ? 'Definitions' : null;
  const patternObj = PATTERNS.find(p => p.id === pattern);

  function validateWordCount(size) {
    const minWords = size === 4 ? 25 : 40;
    if (selectedVocabItem && selectedVocabItem.word_count < minWords) {
      const name = usingCustom ? selectedVocabItem.name : selectedVocabItem.title;
      setError(`"${name}" only has ${selectedVocabItem.word_count} vocabulary words — need at least ${minWords} for a ${size}×${size} board.`);
      return false;
    }
    return true;
  }

  function handleCreate(replaceExisting = false) {
    if (!hasSelection) {
      setNovelError(true);
      novelSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (!validateWordCount(boardSize)) return;

    setError('');
    setExistingGameCode(null);
    setNovelError(false);
    setCreating(true);

    const effectiveFreeSpace = boardSize === 5 && !!freeSpace;
    const payload = usingCustom
      ? { customListId: selectedCustomListId, novelTitle: selectedSourceLabel || '', teacherName: teacher?.classroom_name || 'Teacher', boardSize, freeSpace: effectiveFreeSpace, displayMode, pattern, replaceExisting }
      : { novelId: selectedNovelId, novelTitle: selectedVocabItem?.title || '', teacherName: teacher?.classroom_name || 'Teacher', boardSize, freeSpace: effectiveFreeSpace, displayMode, pattern, replaceExisting };

    const emit = () => {
      socket.emit(
        'game:create',
        payload,
        (res) => {
          if (res.activeRoomCode) {
            // Spec Q8: one game per teacher — ask whether to rejoin or end it
            setExistingGameCode(res.activeRoomCode);
            setCreating(false);
            socket.disconnect();
          } else if (res.error) {
            setError(res.error);
            setCreating(false);
            socket.disconnect();
          } else {
            setActiveRoomCode(res.code);
            navigate(`/host/${res.code}`, { state: { totalWords: res.totalWords, boardSize: res.boardSize, displayMode: res.displayMode, pattern: res.pattern } });
          }
        }
      );
    };

    if (socket.connected) {
      emit();
    } else {
      const onConnect = () => {
        pendingConnectRef.current = null;
        pendingConnectErrorRef.current = null;
        socket.off('connect_error', onConnectError);
        emit();
      };
      const onConnectError = () => {
        pendingConnectRef.current = null;
        pendingConnectErrorRef.current = null;
        socket.off('connect', onConnect);
        setError('Could not connect to the game server. Please try again.');
        setCreating(false);
      };
      pendingConnectRef.current = onConnect;
      pendingConnectErrorRef.current = onConnectError;
      socket.once('connect', onConnect);
      socket.once('connect_error', onConnectError);
      socket.connect();
    }
  }

  function handleSelectBoardSize(size) {
    if (!validateWordCount(size)) return;

    setError('');
    setBoardSize(size);
    setFreeSpace(null);
    if (pattern) {
      const p = PATTERNS.find((p) => p.id === pattern);
      if (p && !p.sizes.includes(size)) setPattern(null);
    }
  }

  const showBoardSize = hasSelection;
  const boardStepComplete = boardSize === 4 || (boardSize === 5 && freeSpace !== null);
  const showDisplayMode = boardStepComplete;
  const showWinPattern = showDisplayMode && displayMode !== null;
  const allComplete = showWinPattern && pattern !== null;

  useEffect(() => {
    if (showBoardSize && !scrolledRef.current.board) {
      scrolledRef.current.board = true;
      boardSizeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showBoardSize]);

  useEffect(() => {
    if (showDisplayMode && !scrolledRef.current.display) {
      scrolledRef.current.display = true;
      displayModeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showDisplayMode]);

  useEffect(() => {
    if (showWinPattern && !scrolledRef.current.pattern) {
      scrolledRef.current.pattern = true;
      winPatternSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showWinPattern]);

  const missingStep = !hasSelection
    ? 'Select a vocabulary source'
    : boardSize === null
    ? 'Choose a board size'
    : boardSize === 5 && freeSpace === null
    ? 'Choose a FREE space option'
    : displayMode === null
    ? 'Choose what students see'
    : pattern === null
    ? 'Choose a win pattern'
    : null;

  if (loading) {
    return (
      <DashboardLayout>
        <p style={styles.muted}>Loading library…</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <style>{`
        .setup-opt:hover, .setup-pattern:hover { border-color: var(--color-primary) !important; background-color: color-mix(in srgb, var(--color-surface) 20%, var(--color-background)) !important; }
        .setup-novel:not(:disabled):hover { border-color: var(--color-primary) !important; background-color: color-mix(in srgb, var(--color-surface) 15%, var(--color-background)) !important; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
        .setup-create:not(:disabled):hover { transform: translateY(-1px) !important; box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important; }
        .customize-nudge-link:hover { text-decoration: underline !important; }
        .novel-grid-outer { position: relative; }
        .novel-grid-outer::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 52px;
          background: linear-gradient(to bottom, transparent, var(--color-background));
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 1;
        }
        .novel-grid-outer.at-bottom::after { opacity: 0; }
      `}</style>

      <div style={styles.header}>
        <h1 style={styles.heading}>New Game</h1>
        <p style={styles.sub}>Set up your vocabulary bingo game and share the room code with your class.</p>
      </div>

      <div style={styles.pageLayout}>

        {/* ── Main form column ── */}
        <div style={styles.mainCol}>

          {/* 1. Vocabulary source — primary decision */}
          <div
            ref={novelSectionRef}
            style={{
              ...styles.sectionBox,
              ...(novelError ? styles.sectionBoxError : {}),
            }}
          >
            <div style={styles.sectionTitleRow}>
              <span style={styles.sectionLabel}>Vocabulary source</span>
              {novelError && (
                <span style={styles.inlineError}>Please select a novel or list to continue</span>
              )}
            </div>

            {isSubscriber && (
              <div style={{ ...styles.optRow, marginBottom: '16px' }}>
                <button
                  className="setup-opt"
                  onClick={() => {
                    setListSource('novel');
                    setSelectedCustomListId('');
                    setError('');
                    setNovelError(false);
                  }}
                  style={{ ...styles.sizeBtn, ...(listSource === 'novel' ? styles.sizeBtnActive : {}) }}
                >
                  Novel Library
                  <span style={styles.sizeBtnSub}>Built-in novel word lists</span>
                </button>
                <button
                  className="setup-opt"
                  onClick={() => {
                    setListSource('custom');
                    setSelectedNovelId('');
                    setError('');
                    setNovelError(false);
                  }}
                  style={{ ...styles.sizeBtn, ...(listSource === 'custom' ? styles.sizeBtnActive : {}) }}
                >
                  My Lists
                  <span style={styles.sizeBtnSub}>Your custom vocabulary lists</span>
                </button>
              </div>
            )}

            {listSource === 'custom' && (
              customLists.length === 0 ? (
                <div style={styles.emptyCard}>
                  <p style={styles.emptyText}>
                    You haven't created any custom vocabulary lists yet.{' '}
                    <a href="/lists/new" style={{ color: 'var(--color-primary)', fontWeight: '600' }}>Create your first list →</a>
                  </p>
                </div>
              ) : (
                <div className={`novel-grid-outer${gridAtBottom ? ' at-bottom' : ''}`}>
                <div ref={gridScrollRef} style={styles.novelGridScroll} onScroll={handleGridScroll}>
                <div style={styles.novelGrid}>
                  {customLists.map((list) => (
                    <VocabCard
                      key={list.id}
                      title={list.name}
                      wordCount={list.word_count}
                      selected={selectedCustomListId === list.id}
                      onSelect={() => {
                        setSelectedCustomListId(list.id);
                        setNovelError(false);
                      }}
                    />
                  ))}
                </div>
                </div>
                </div>
              )
            )}

            {listSource === 'novel' && (novels.length === 0 ? (
              <div style={styles.emptyCard}>
                <p style={styles.emptyText}>
                  No novels are available yet. The library is being set up — check back shortly, or ask
                  your administrator to run the seed script.
                </p>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title, author, or grade…"
                  style={styles.searchInput}
                />
                <div className={`novel-grid-outer${gridAtBottom ? ' at-bottom' : ''}`}>
                <div ref={gridScrollRef} style={styles.novelGridScroll} onScroll={handleGridScroll}>
                <div style={styles.novelGrid}>
                  {novels
                    .filter((n) => {
                      const q = search.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        n.title.toLowerCase().includes(q) ||
                        (n.author || '').toLowerCase().includes(q) ||
                        (n.grade_range || '').toLowerCase().includes(q)
                      );
                    })
                    .map((novel) => (
                      <VocabCard
                        key={novel.id}
                        title={novel.title}
                        meta={`${novel.author}${novel.grade_range ? ` · ${novel.grade_range}` : ''}`}
                        wordCount={novel.word_count}
                        selected={selectedNovelId === novel.id}
                        onSelect={() => {
                          setSelectedNovelId(novel.id);
                          setNovelError(false);
                        }}
                      />
                    ))}
                </div>
                </div>
                </div>
                {selectedNovelId && (
                  <p style={styles.customizeNudge}>
                    Want to customize this list?{' '}
                    {isSubscriber ? (
                      <button
                        className="customize-nudge-link"
                        onClick={() => {
                          if (selectedVocabItem) handleCustomize(selectedVocabItem);
                        }}
                        disabled={cloning}
                        style={styles.customizeNudgeLink}
                      >
                        {cloning ? 'Creating custom version…' : 'Create a custom version →'}
                      </button>
                    ) : (
                      <a href="/subscribe" style={styles.customizeNudgeLink}>
                        Create a custom version →
                      </a>
                    )}
                  </p>
                )}
              </>
            ))}
          </div>

          {/* 2. Board size with inline FREE space */}
          {showBoardSize && (
            <div ref={boardSizeSectionRef} style={styles.sectionBox}>
              <div style={styles.sectionLabel}>Board size</div>
              <div style={styles.optRow}>
                {[5, 4].map((s) => (
                  <button
                    key={s}
                    className="setup-opt"
                    onClick={() => handleSelectBoardSize(s)}
                    style={{ ...styles.sizeBtn, ...(boardSize === s ? styles.sizeBtnActive : {}) }}
                  >
                    {s}×{s}
                    <span style={styles.sizeBtnSub}>
                      {s === 5 ? '40+ words required' : '25+ words · no FREE space'}
                    </span>
                  </button>
                ))}
              </div>
              {boardSize === 5 && (
                <div style={{ marginTop: '14px' }}>
                  <div style={styles.subLabel}>Include a FREE space in the center?</div>
                  <div style={styles.optRow}>
                    <button
                      className="setup-opt"
                      onClick={() => setFreeSpace(true)}
                      style={{ ...styles.sizeBtn, ...(freeSpace === true ? styles.sizeBtnActive : {}) }}
                    >
                      Yes, include FREE space
                    </button>
                    <button
                      className="setup-opt"
                      onClick={() => setFreeSpace(false)}
                      style={{ ...styles.sizeBtn, ...(freeSpace === false ? styles.sizeBtnActive : {}) }}
                    >
                      No FREE space
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 3. Card display mode */}
          {showDisplayMode && (
            <div ref={displayModeSectionRef} style={styles.sectionBox}>
              <div style={styles.sectionLabel}>What will students see on their cards?</div>
              <div style={styles.optRow}>
                {[
                  { value: 'word',       label: 'Words',       sub: "You'll read definitions aloud" },
                  { value: 'definition', label: 'Definitions', sub: "You'll read words aloud" },
                ].map(({ value, label, sub }) => (
                  <button
                    key={value}
                    className="setup-opt"
                    onClick={() => setDisplayMode(value)}
                    style={{ ...styles.sizeBtn, ...(displayMode === value ? styles.sizeBtnActive : {}) }}
                  >
                    {label}
                    <span style={styles.sizeBtnSub}>{sub}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 4. Win pattern with hover tooltips */}
          {showWinPattern && (
            <div ref={winPatternSectionRef} style={styles.sectionBox}>
              <div style={styles.sectionLabel}>Win pattern</div>
              <div style={styles.patternGrid}>
                {PATTERNS.filter(p => p.sizes.includes(boardSize)).map(({ id, label, description }) => (
                  <div
                    key={id}
                    style={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredPattern(id)}
                    onMouseLeave={() => setHoveredPattern('')}
                  >
                    <button
                      className="setup-pattern"
                      onClick={() => setPattern(id)}
                      style={{ ...styles.patternBtn, ...(pattern === id ? styles.patternBtnActive : {}), width: '100%' }}
                    >
                      <PatternPreview pattern={id} size={boardSize} freeSpace={boardSize === 5 && freeSpace} cellSize={14} />
                      <span style={styles.patternBtnLabel}>{label}</span>
                      <span style={styles.patternBtnDesc}>{description}</span>
                    </button>
                    {hoveredPattern === id && (
                      <div style={styles.patternTooltip}>
                        <PatternPreview pattern={id} size={boardSize} freeSpace={boardSize === 5 && freeSpace} cellSize={22} />
                        <div style={styles.patternTooltipTitle}>{label}</div>
                        <div style={styles.patternTooltipDesc}>{description}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ── Summary sidebar ── */}
        <aside style={styles.sidebar}>
          <div style={styles.summaryCard}>
            <h3 style={styles.summaryHeading}>Game Summary</h3>

            <div style={styles.summaryRows}>
              <div style={styles.summaryRow}>
                <span style={styles.summaryRowLabel}>Vocabulary</span>
                <span style={hasSelection ? styles.summaryRowValue : styles.summaryRowMissing}>
                  {selectedSourceLabel ?? 'Not selected'}
                </span>
              </div>
              {showBoardSize && (
                <div style={styles.summaryRow}>
                  <span style={styles.summaryRowLabel}>Board</span>
                  <span style={boardSize ? styles.summaryRowValue : styles.summaryRowMissing}>
                    {boardSize ? `${boardSize}×${boardSize}` : 'Not selected'}
                  </span>
                </div>
              )}
              {boardSize === 5 && (
                <div style={styles.summaryRow}>
                  <span style={styles.summaryRowLabel}>FREE space</span>
                  <span style={freeSpace !== null ? styles.summaryRowValue : styles.summaryRowMissing}>
                    {freeSpace === null ? 'Not selected' : freeSpace ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {showDisplayMode && (
                <div style={styles.summaryRow}>
                  <span style={styles.summaryRowLabel}>Cards show</span>
                  <span style={displayMode ? styles.summaryRowValue : styles.summaryRowMissing}>
                    {displayModeLabel ?? 'Not selected'}
                  </span>
                </div>
              )}
              {showWinPattern && (
                <div style={{ ...styles.summaryRow, borderBottom: 'none' }}>
                  <span style={styles.summaryRowLabel}>Win pattern</span>
                  <span style={pattern ? styles.summaryRowValue : styles.summaryRowMissing}>
                    {patternObj?.label ?? 'Not selected'}
                  </span>
                </div>
              )}
            </div>

            {error && <p style={styles.error}>{error}</p>}

            {existingGameCode && (
              <div style={styles.existingGame}>
                <p style={styles.existingGameText}>
                  You already have a game in progress ({existingGameCode}). End it to start this one?
                </p>
                <div style={styles.existingGameActions}>
                  <button
                    style={styles.existingGameBtn}
                    onClick={() => {
                      setActiveRoomCode(existingGameCode);
                      navigate(`/host/${existingGameCode}`);
                    }}
                  >
                    Rejoin current game
                  </button>
                  <button
                    style={{ ...styles.existingGameBtn, ...styles.existingGameBtnDanger }}
                    onClick={() => handleCreate(true)}
                  >
                    End it and start new
                  </button>
                </div>
              </div>
            )}

            <button
              className="setup-create"
              onClick={() => handleCreate()}
              disabled={!allComplete || creating}
              style={{
                ...styles.createBtn,
                ...(allComplete && !creating ? styles.createBtnActive : styles.createBtnDisabled),
              }}
            >
              {creating ? 'Creating room…' : missingStep ?? 'Create Game →'}
            </button>

            {!allComplete && !creating && (
              <p style={styles.createHint}>Complete each step above, in order, to unlock this button.</p>
            )}
          </div>
        </aside>

      </div>
    </DashboardLayout>
  );
}
